import { useRequestApplePayPayment } from 'features/payment/integrations/apple-pay';
import { useRequestBraintreePayment } from 'features/payment/integrations/braintree';
import { useRequestCashAppPayment } from 'features/payment/integrations/cash-app';
import { useFirstDataCardPayment } from 'features/payment/integrations/first-data';
import { useRequestGooglePayPayment } from 'features/payment/integrations/google-pay';
import { IPaymentDetails, RequestPaymentResult } from 'features/payment/types';

import { useCheckoutContext } from '../use-checkout-context';

/**
 * Aggregates payment details from various payment providers based on the current checkout state.
 *
 * Each provider's `requestPayment()` returns a `Promise<IPaymentDetails | undefined>`:
 * - Resolves with payment details on success
 * - Resolves with `undefined` or rejects (in one case) if the user cancels/dismisses
 * - Rejects with `PaymentRequestError` on failure
 *
 * This enables an imperative flow in the checkout submit: `const details = await requestPayment()`.
 */
type UseRequestPaymentDetailsOptions = {
  onCashAppRedirectComplete: (details: IPaymentDetails) => Promise<void> | void;
  onCashAppRedirectStateChange: (isPending: boolean) => void;
};

export const useRequestPaymentDetails = ({
  onCashAppRedirectComplete,
  onCashAppRedirectStateChange,
}: UseRequestPaymentDetailsOptions): RequestPaymentResult => {
  const {
    billingCountry,
    showReloadPaymentMethods,
    paymentValues,
    reloadPaymentValues,
    isPaymentProcessor,
    isPaymentType,
    totals,
    serverOrder,
    cartEntries,
  } = useCheckoutContext();
  const cardPayment = useFirstDataCardPayment({
    firsDataCard: {
      paymentValues: showReloadPaymentMethods ? reloadPaymentValues : paymentValues,
    },
  });
  const cashAppPayment = useRequestCashAppPayment({
    cashapp: {
      total: totals?.grandTotal || 0,
      tax: totals?.taxCents ?? 0,
      tip: totals?.tipAmount ?? 0,
      rbiOrderId: serverOrder?.rbiOrderId,
      onRedirectComplete: onCashAppRedirectComplete,
      onRedirectStateChange: onCashAppRedirectStateChange,
    },
  });
  const braintreePayment = useRequestBraintreePayment({});
  const googlePayment = useRequestGooglePayPayment({
    googlePay: { billingCountry, total: totals?.grandTotal ?? 0, reloadAmount: 0 },
  });
  const applePayment = useRequestApplePayPayment({
    applePay: {
      serverOrder,
      cartEntries,
      tip: totals?.tipAmount ?? 0,
      reloadAmount: 0,
      billingCountry,
    },
  });

  if (isPaymentProcessor.braintree) {
    return braintreePayment;
  }

  if (isPaymentType.cashApp) {
    return cashAppPayment;
  }

  if (isPaymentType.googlePay) {
    return googlePayment;
  }

  if (isPaymentType.applePay) {
    return applePayment;
  }

  return cardPayment;
};
